import Logo from "@/components/Logo.js";
import { SnackbarProvider } from "@/context/SnackbarContext.js";
import { useFirebaseAuth } from "@/utils/firebase.js";
import { initDevTools } from "@/utils/inits.js";
import {
  Box,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  CalendarIcon,
  CurrencyDollarIcon,
  FilesIcon,
  GearIcon,
  GraduationCapIcon,
  PaperPlaneTiltIcon,
  StudentIcon,
} from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useCommonCurrentUser, useCurrentStaffUser } from "../utils/api.js";
import { signOut } from "../utils/firebase.js";
import MagicLoading from "./MagicLoading.js";

// Define the sidebar width
const drawerWidth = 280;

// Interface for navigation item
interface NavItem {
  title: string;
  path: string;
  icon: React.ReactNode;
}

// Interface for navigation section
interface NavSection {
  title: string;
  items: NavItem[];
}

// Create a styled component for the main content
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  height: "100vh",
  overflow: "auto",
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

/**
 * Layout for school management system pages
 * Forces users to be staff and redirects appropriately based on staff profile completion
 */
const SchoolLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user: firebaseUser, isInitialized } = useFirebaseAuth();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));

  // Use common endpoint for routing decisions
  const { data: commonUser, isLoading: isCommonLoading, isError: isCommonError } = useCommonCurrentUser({
    enabled: isInitialized && !!firebaseUser,
    showErrorSnackbar: false
  });

  // Use staff-specific endpoint only when we know user is staff
  const {
    data: user,
    isLoading: isStaffLoading,
    isError: isStaffError,
    error,
  } = useCurrentStaffUser({
    enabled: isInitialized && !!firebaseUser && commonUser?.data?.userType === 'staff',
    showErrorSnackbar: false,
  });

  const isLoading = isCommonLoading || (commonUser?.data?.userType === 'staff' && isStaffLoading);
  const isError = isCommonError || (commonUser?.data?.userType === 'staff' && isStaffError);

  useEffect(() => {
    if (!isInitialized) return;

    // If no Firebase user, redirect to login
    if (!firebaseUser) {
      navigate("/school/auth/login");
      return;
    }

    if (isLoading) return;

    // Check user role first with common endpoint
    if (commonUser?.data) {
      if (commonUser.data.cookies?.developerAid) {
        initDevTools();
      }

      if (commonUser.data.userType === 'student') {
        // User is a student trying to access staff area, redirect to convert page
        navigate("/school/auth/existingUser");
        return;
      }
    }

    // If error indicates user is not staff, redirect to convert page
    if (
      error &&
      error.data?.code === "FORBIDDEN" &&
      error.message.startsWith("Access denied.")
    ) {
      navigate("/school/auth/existingUser");
      return;
    }

    if (isError) {
      navigate("/school/auth/login");
      return;
    }

    // If user exists but no staff profile, redirect to setup
    if (user && !user.data?.staffProfile) {
      navigate("/school/auth/signup/details");
      return;
    }

    // User is valid staff with profile - continue to requested page
  }, [
    user,
    commonUser,
    location.pathname,
    location.search,
    isLoading,
    isError,
    firebaseUser,
    isInitialized,
    error,
    navigate,
  ]);

  const handleLogout = () => {
    signOut();
    queryClient.clear();
    navigate("/school/auth/login");
  };

  // Navigation sections for school management
  const navSections: NavSection[] = useMemo(
    () => [
      {
        title: "School",
        items: [
          {
            title: "Setup School",
            path: "/school/app/organization",
            icon: <GearIcon size={24} />,
          },
          {
            title: "Invite Users",
            path: "/school/app/invitations",
            icon: <PaperPlaneTiltIcon size={24} />,
          },
          {
            title: "Student Overview",
            path: "/school/app/students",
            icon: <StudentIcon size={24} />,
          },
          {
            title: "Calendar Management",
            path: "/school/app/calendar",
            icon: <CalendarIcon size={24} />,
          },
          {
            title: "Document Requests",
            path: "/school/app/document-requests",
            icon: <FilesIcon size={24} />,
          }
        ],
      },
      {
        title: "Search",
        items: [
          {
            title: "Search Courses",
            path: "/school/app/courses/search",
            icon: <GraduationCapIcon size={24} />,
          },
          {
            title: "Search Scholarships",
            path: "/school/app/scholarships/search",
            icon: <CurrencyDollarIcon size={24} />,
          }
        ],
      },
    ],
    [],
  );

  // Show loading while checking auth
  if (isLoading || !isInitialized) {
    return (
      <MagicLoading
        active
        onMagic={() => {
          signOut();
          navigate("/school/auth/login");
        }}
      />
    );
  }

  // Mobile not supported message
  if (isMobile) {
    return (
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        p={2}
        textAlign="center"
      >
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          School Management System
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Mobile support is coming soon. For the best experience, please use a
          desktop or laptop browser.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We appreciate your patience as we build a better mobile experience!
        </Typography>
      </Stack>
    );
  }

  const drawer = (
    <Stack sx={{ height: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{
          padding: 2,
          height: 80,
        }}
      >
        <Logo />
      </Stack>

      <Stack sx={{ overflow: "hidden", height: "100%", padding: 2 }}>
        <Box sx={{ overflow: "auto" }}>
          {navSections.map((section) => (
            <Box key={section.title}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: "1.1rem",
                  color: "text.primary",
                  mb: 1,
                }}
              >
                {section.title}
              </Typography>
              <List sx={{ pl: 0, pb: 2 }}>
                {section.items.map((item) => {
                  const isSelected = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                  return (
                    <ListItem key={item.path} disablePadding>
                      <ListItemButton
                        component={Link}
                        to={item.path}
                        sx={{
                          borderRadius: "8px",
                          py: 0.5,
                          px: 1,
                          my: 0.5,
                          backgroundColor: isSelected
                            ? "rgba(242, 242, 242, 0.8)"
                            : "transparent",
                          "&:hover": {
                            backgroundColor: isSelected
                              ? "rgba(242, 242, 242, 0.8)"
                              : "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            color: isSelected
                              ? "primary.main"
                              : "text.secondary",
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.title}
                          slotProps={{
                            primary: {
                              color: "text.primary",
                            },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>

        <Box flex={1} />

        {/* User info and logout */}
        <Box sx={{ p: 2, borderTop: "1px solid rgba(0, 0, 0, 0.1)" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {user?.data?.firstName} {user?.data?.lastName}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 2, display: "block" }}
          >
            {user?.data?.staffProfile?.jobTitle}
          </Typography>
          <Button
            onClick={handleLogout}
            variant="outlined"
            size="small"
            fullWidth
            sx={{ borderRadius: "8px" }}
          >
            Logout
          </Button>
        </Box>
      </Stack>
    </Stack>
  );

  return (
    <SnackbarProvider>
      <Stack direction="row">
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            height: "100%",
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              border: "none",
              borderRight: "1px solid rgba(0, 0, 0, 0.1)",
              boxShadow: "none",
              height: "100%",
            },
          }}
          variant="persistent"
          anchor="left"
          open={true}
        >
          {drawer}
        </Drawer>
        <Main open={true} sx={{ padding: { xs: 2, md: 3, lg: 4, xl: 6 } }}>
          <Outlet />
        </Main>
      </Stack>
    </SnackbarProvider>
  );
};

export default SchoolLayout;
